import { useCallback } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { Lead } from "../../types/lead";
import type { Location } from "../../types/location";
import type { OrgLocation } from "../../config/locations";
import { textHeuristics } from "@/lib/lead/extraction/textHeuristics";
import { evaluateDuplicates } from "@/lib/lead/merge/duplicates";
import { handleSendLeadClick } from "@/lib/lead/actions/sendLeadFlow";
import {
  leadMatchesWorkspaceLocation,
  shouldPromptGhlLocationMismatch,
} from "@/lib/lead/locationScope";

type MergeSuggestion = {
  sourceId: string;
  reason: string;
};

type UseLeadGhlSendFlowParams = {
  leadsRef: MutableRefObject<Lead[]>;
  locationRef: MutableRefObject<Location | null>;
  locations: OrgLocation[];
  location: Location | null;
  locationScopedLeads: Lead[];
  pendingLocationSend: Lead | null;
  setPendingLocationSend: Dispatch<SetStateAction<Lead | null>>;
  setPendingSendLead: Dispatch<SetStateAction<Lead | null>>;
  setDuplicateMatches: Dispatch<SetStateAction<Lead[]>>;
  setDuplicateReason: Dispatch<SetStateAction<string | null>>;
  setMergeSuggestion: Dispatch<SetStateAction<MergeSuggestion | null>>;
  canSpeakHelp: boolean;
  speak: ((text: string) => void) | undefined;
  sendToGHL: (lead: Lead, opts?: { locationOverride?: Location }) => Promise<void>;
};

export function useLeadGhlSendFlow(params: UseLeadGhlSendFlowParams) {
  const getScopedLeadsPool = useCallback(
    () => params.leadsRef.current.filter((l) => leadMatchesWorkspaceLocation(l, params.locationRef.current?.id)),
    [params.leadsRef, params.locationRef]
  );

  const resolveOrgLocationForOrigin = useCallback(
    (lead: Lead): Location => {
      const id = (lead.origin_location_id || "").trim();
      const fromList = params.locations.find((l) => l.id === id);
      if (fromList) {
        return {
          id: fromList.id,
          name: fromList.fullName,
          address: fromList.address ?? "",
          phone: fromList.phone ?? "",
        };
      }
      return {
        id,
        name: (lead.origin_location_name || "").trim() || id || "Origin location",
        address: "",
        phone: "",
      };
    },
    [params.locations]
  );

  const guardedSendToGhl = useCallback(
    (lead: Lead, opts?: { locationOverride?: Location | null; skipLocationPrompt?: boolean }) => {
      if (!opts?.skipLocationPrompt && !opts?.locationOverride && shouldPromptGhlLocationMismatch(lead, params.location)) {
        params.setPendingLocationSend(lead);
        return;
      }
      handleSendLeadClick(lead, {
        evaluateDuplicates: (l) =>
          evaluateDuplicates(l, params.locationScopedLeads, { normalizePhone: textHeuristics.normalizePhone }),
        sendToGHL: (l) => params.sendToGHL(l, { locationOverride: opts?.locationOverride ?? undefined }),
        setPendingSendLead: params.setPendingSendLead,
        setDuplicateMatches: params.setDuplicateMatches,
        setDuplicateReason: params.setDuplicateReason,
        setMergeSuggestion: params.setMergeSuggestion,
        canSpeakHelp: params.canSpeakHelp,
        speak: params.canSpeakHelp ? params.speak : undefined,
      });
    },
    [params]
  );

  const handleSendClick = useCallback((lead: Lead) => guardedSendToGhl(lead, {}), [guardedSendToGhl]);

  const completeGhlSendUsingOriginLocation = useCallback(() => {
    const lead = params.pendingLocationSend;
    if (!lead) return;
    params.setPendingLocationSend(null);
    guardedSendToGhl(lead, { locationOverride: resolveOrgLocationForOrigin(lead) });
  }, [guardedSendToGhl, params.pendingLocationSend, params.setPendingLocationSend, resolveOrgLocationForOrigin]);

  const completeGhlSendUsingWorkspaceLocation = useCallback(() => {
    const lead = params.pendingLocationSend;
    if (!lead) return;
    params.setPendingLocationSend(null);
    guardedSendToGhl(lead, { skipLocationPrompt: true });
  }, [guardedSendToGhl, params.pendingLocationSend, params.setPendingLocationSend]);

  return {
    getScopedLeadsPool,
    resolveOrgLocationForOrigin,
    guardedSendToGhl,
    handleSendClick,
    completeGhlSendUsingOriginLocation,
    completeGhlSendUsingWorkspaceLocation,
  };
}
